import React, { useState, useEffect, useCallback, useRef } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, X, Plus, Download, Trash2, Copy, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { format, startOfWeek, addDays, addWeeks, subWeeks } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS = {
  monday: 'Segunda',
  tuesday: 'Terça',
  wednesday: 'Quarta',
  thursday: 'Quinta',
  friday: 'Sexta',
  saturday: 'Sábado',
  sunday: 'Domingo'
};

// Shift type presets
const SHIFT_PRESETS = [
  { label: 'Abertura', start: 'Abertura', end: '14:00' },
  { label: 'Fecho', start: '14:00', end: 'Fecho' },
  { label: 'Manhã (08:00 - 14:00)', start: '08:00', end: '14:00' },
  { label: 'Tarde (16:00 - 01:00)', start: '16:00', end: '01:00' },
  { label: 'Noite (17:00 - 02:00)', start: '17:00', end: '02:00' },
  { label: 'Integral (09:00 - 18:00)', start: '09:00', end: '18:00' },
  { label: 'Personalizado', start: '', end: '' },
];

const DEPT_LABELS = { sala: 'SALA', bar: 'BAR', cozinha: 'COZINHA' };

export default function Schedules() {
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [employees, setEmployees] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [currentWeek, setCurrentWeek] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [loading, setLoading] = useState(true);
  
  // Edit dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [editingDay, setEditingDay] = useState(null);
  const [editingDepartment, setEditingDepartment] = useState(null); // setor do card clicado (se multi)
  const [dayType, setDayType] = useState('working'); // 'working' | 'folga' | 'vacation' | 'absence'
  const [seg1Start, setSeg1Start] = useState('09:00');
  const [seg1End, setSeg1End] = useState('18:00');
  const [seg1Dept, setSeg1Dept] = useState('');
  const [hasSeg2, setHasSeg2] = useState(false);
  const [seg2Start, setSeg2Start] = useState('16:00');
  const [seg2End, setSeg2End] = useState('01:00');
  const [seg2Dept, setSeg2Dept] = useState('');
  const [isAbertura, setIsAbertura] = useState(false);
  const [isFecho, setIsFecho] = useState(false);
  const fileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    fetchLocations();
  }, []);

  useEffect(() => {
    if (selectedLocation) {
      fetchEmployees();
      fetchSchedules();
    }
  }, [selectedLocation, currentWeek]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchLocations = async () => {
    try {
      const res = await axios.get(`${API}/locations`, { withCredentials: true });
      setLocations(res.data);
      if (res.data.length > 0) {
        setSelectedLocation(res.data[0].location_id);
      }
    } catch (error) {
      toast.error('Erro ao carregar locais');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await axios.get(`${API}/employees?location_id=${selectedLocation}&status=active`, { withCredentials: true });
      setEmployees(res.data);
    } catch (error) {
      toast.error('Erro ao carregar funcionários');
    }
  };

  const fetchSchedules = async () => {
    const weekStart = format(currentWeek, 'yyyy-MM-dd');
    try {
      const res = await axios.get(`${API}/schedules?location_id=${selectedLocation}&week_start=${weekStart}`, { withCredentials: true });
      setSchedules(res.data);
    } catch (error) {
      toast.error('Erro ao carregar escalas');
    }
  };

  const getScheduleForEmployee = (employeeId) => {
    return schedules.find(s => s.employee_id === employeeId);
  };

  const getShiftForDay = (employeeId, day) => {
    const schedule = getScheduleForEmployee(employeeId);
    return schedule?.shifts?.[day] || null;
  };

  // Retorna os segmentos normalizados de um shift (handles legacy e novo formato)
  const getSegments = (shift) => {
    if (!shift) return [];
    if (shift.is_folga || shift.type === 'folga') return [];
    if (Array.isArray(shift.segments) && shift.segments.length > 0) return shift.segments;
    // Legado: start/end no nível superior
    if (shift.start || shift.end) {
      return [{ start: shift.start || '', end: shift.end || '', department: null }];
    }
    return [];
  };

  // Filtra segmentos que pertencem a um setor específico (ou sem setor marcado = todos)
  const getSegmentsForDept = (shift, deptKey) => {
    const segs = getSegments(shift);
    if (!deptKey) return segs;
    // Se algum segmento tem department definido, respeitar filtro; senão mostra todos (compat)
    const anyTagged = segs.some((s) => s.department);
    if (!anyTagged) return segs;
    return segs.filter((s) => !s.department || s.department === deptKey);
  };

  const openEditDialog = (employeeId, day, departmentKey = null) => {
    const shift = getShiftForDay(employeeId, day);
    const employee = employees.find((e) => e.employee_id === employeeId);
    const empDepts = (Array.isArray(employee?.departments) && employee.departments.length > 0)
      ? employee.departments
      : (employee?.department ? [employee.department] : []);
    const defaultDept = departmentKey || empDepts[0] || '';

    setEditingEmployee(employeeId);
    setEditingDay(day);
    setEditingDepartment(departmentKey);

    // reset
    setDayType('working');
    setIsAbertura(false);
    setIsFecho(false);
    setHasSeg2(false);
    setSeg1Start('09:00'); setSeg1End('18:00'); setSeg1Dept(defaultDept);
    setSeg2Start('16:00'); setSeg2End('01:00'); setSeg2Dept(defaultDept);

    if (shift) {
      if (shift.is_folga || shift.type === 'folga') {
        setDayType('folga');
      } else if (shift.type === 'vacation') {
        setDayType('vacation');
      } else if (shift.type === 'absence') {
        setDayType('absence');
      } else {
        const segs = getSegments(shift);
        if (segs[0]) {
          const startIsAbertura = segs[0].start === 'Abertura';
          const endIsFecho = segs[0].end === 'Fecho';
          setIsAbertura(startIsAbertura);
          setIsFecho(endIsFecho);
          setSeg1Start(startIsAbertura ? '09:00' : (segs[0].start || '09:00'));
          setSeg1End(endIsFecho ? '22:00' : (segs[0].end || '18:00'));
          setSeg1Dept(segs[0].department || defaultDept);
        }
        if (segs[1]) {
          setHasSeg2(true);
          setSeg2Start(segs[1].start || '16:00');
          setSeg2End(segs[1].end || '01:00');
          setSeg2Dept(segs[1].department || defaultDept);
        }
      }
    }
    setDialogOpen(true);
  };

  const saveShift = async () => {
    if (!editingEmployee || !editingDay) return;

    const schedule = getScheduleForEmployee(editingEmployee);
    const weekStart = format(currentWeek, 'yyyy-MM-dd');
    const newShifts = schedule?.shifts ? { ...schedule.shifts } : {};

    if (dayType === 'folga') {
      newShifts[editingDay] = { is_folga: true, segments: [] };
    } else if (dayType === 'vacation') {
      newShifts[editingDay] = { type: 'vacation', segments: [] };
    } else if (dayType === 'absence') {
      newShifts[editingDay] = { type: 'absence', segments: [] };
    } else {
      const segments = [];
      segments.push({
        start: isAbertura ? 'Abertura' : seg1Start,
        end: isFecho ? 'Fecho' : seg1End,
        department: seg1Dept || null,
      });
      if (hasSeg2) {
        segments.push({
          start: seg2Start,
          end: seg2End,
          department: seg2Dept || null,
        });
      }
      newShifts[editingDay] = {
        segments,
        // compat legado: primeiro segmento espelhado em start/end do topo
        start: segments[0].start,
        end: segments[0].end,
      };
    }

    try {
      await axios.post(`${API}/schedules`, {
        location_id: selectedLocation,
        employee_id: editingEmployee,
        week_start: weekStart,
        shifts: newShifts,
      }, { withCredentials: true });

      toast.success('Turno salvo');
      setDialogOpen(false);
      fetchSchedules();
    } catch (error) {
      toast.error('Erro ao salvar turno');
    }
  };

  const removeShift = async () => {
    if (!editingEmployee || !editingDay) return;

    const schedule = getScheduleForEmployee(editingEmployee);
    const weekStart = format(currentWeek, 'yyyy-MM-dd');
    
    if (!schedule) {
      setDialogOpen(false);
      return;
    }

    const newShifts = { ...schedule.shifts };
    delete newShifts[editingDay];

    try {
      await axios.post(`${API}/schedules`, {
        location_id: selectedLocation,
        employee_id: editingEmployee,
        week_start: weekStart,
        shifts: newShifts
      }, { withCredentials: true });

      toast.success('Turno removido');
      setDialogOpen(false);
      fetchSchedules();
    } catch (error) {
      toast.error('Erro ao remover turno');
    }
  };

  const applyPreset = (preset) => {
    setDayType('working');
    if (preset.start === 'Abertura') {
      setIsAbertura(true);
      setSeg1Start('09:00');
    } else {
      setIsAbertura(false);
      setSeg1Start(preset.start || '09:00');
    }
    if (preset.end === 'Fecho') {
      setIsFecho(true);
      setSeg1End('22:00');
    } else {
      setIsFecho(false);
      setSeg1End(preset.end || '18:00');
    }
  };

  const exportScheduleExcel = async () => {
    const weekStart = format(currentWeek, 'yyyy-MM-dd');
    try {
      const res = await axios.get(
        `${API}/reports/schedule-excel?location_id=${selectedLocation}&week_start=${weekStart}`,
        { withCredentials: true, responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `escala_${weekStart}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Escala exportada');
    } catch (error) {
      toast.error('Erro ao exportar escala');
    }
  };

  const copyFromPreviousWeek = async () => {
    const prevWeek = format(subWeeks(currentWeek, 1), 'yyyy-MM-dd');
    const thisWeek = format(currentWeek, 'yyyy-MM-dd');
    try {
      const res = await axios.post(`${API}/schedules/copy-week`, {
        location_id: selectedLocation,
        source_week: prevWeek,
        target_week: thisWeek
      }, { withCredentials: true });
      
      toast.success(`${res.data.copied} escalas copiadas da semana anterior`);
      fetchSchedules();
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Nenhuma escala encontrada na semana anterior');
      } else {
        toast.error('Erro ao copiar escala');
      }
    }
  };

  const handleImportFile = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) return;
    if (!selectedLocation) {
      toast.error('Selecione um local primeiro');
      return;
    }
    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      toast.error('Envie um arquivo .xlsx');
      return;
    }
    const weekStart = format(currentWeek, 'yyyy-MM-dd');
    const formData = new FormData();
    formData.append('file', file);
    formData.append('location_id', selectedLocation);
    formData.append('week_start', weekStart);
    setImporting(true);
    try {
      const res = await axios.post(`${API}/schedules/import`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const { imported, updated, matched_employees, unmatched_names, errors } = res.data;
      const totalSaved = (imported || 0) + (updated || 0);
      if (totalSaved === 0 && matched_employees === 0) {
        toast.error('Nenhum funcionário reconhecido. Verifique os nomes na planilha.');
      } else {
        toast.success(
          `Escala importada: ${matched_employees} funcionário(s) · ${imported} criadas · ${updated} atualizadas`
        );
      }
      if (unmatched_names && unmatched_names.length > 0) {
        toast.warning(`Não reconhecidos (${unmatched_names.length}): ${unmatched_names.slice(0, 5).join(', ')}${unmatched_names.length > 5 ? '…' : ''}`);
      }
      if (errors && errors.length > 0) {
        toast.error(`${errors.length} célula(s) com erro de formato`);
      }
      fetchSchedules();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erro ao importar escala';
      toast.error(typeof msg === 'string' ? msg : 'Erro ao importar escala');
    } finally {
      setImporting(false);
    }
  };

  const formatShiftDisplay = (shift, deptKey = null) => {
    if (!shift) return null;
    if (shift.type === 'vacation') return 'FÉRIAS';
    if (shift.type === 'absence') return 'FALTA';
    if (shift.is_folga || shift.type === 'folga') return 'F';
    const segs = getSegmentsForDept(shift, deptKey);
    if (segs.length === 0) return null;
    return segs.map((s) => {
      const start = s.start === 'Abertura' ? 'ABR' : s.start;
      const end = s.end === 'Fecho' ? 'FCH' : s.end;
      return `${start} - ${end}`;
    });
  };

  if (loading) {
    return (
      <AdminLayout title="Escalas">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[#111111] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Escalas">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <div className="flex items-center gap-2">
          <Label className="text-xs uppercase tracking-widest">Local:</Label>
          <Select value={selectedLocation} onValueChange={setSelectedLocation}>
            <SelectTrigger className="w-[180px]" data-testid="schedule-location-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {locations.map(loc => (
                <SelectItem key={loc.location_id} value={loc.location_id}>
                  {loc.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="icon"
            onClick={() => setCurrentWeek(subWeeks(currentWeek, 1))}
            data-testid="prev-week-btn"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm font-medium min-w-[200px] text-center">
            {format(currentWeek, "dd 'de' MMMM", { locale: ptBR })} - {format(addDays(currentWeek, 6), "dd 'de' MMMM, yyyy", { locale: ptBR })}
          </span>
          <Button 
            variant="outline" 
            size="icon"
            onClick={() => setCurrentWeek(addWeeks(currentWeek, 1))}
            data-testid="next-week-btn"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>

        <div className="flex items-center gap-2 ml-auto">
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx"
            className="hidden"
            onChange={handleImportFile}
            data-testid="import-schedule-file-input"
          />
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={importing || !selectedLocation}
            data-testid="import-schedule-btn"
            className="text-xs"
          >
            <Upload className="w-4 h-4 mr-1" />
            {importing ? 'Importando…' : 'Importar Escala'}
          </Button>
          <Button variant="outline" onClick={copyFromPreviousWeek} data-testid="copy-week-btn" className="text-xs">
            <Copy className="w-4 h-4 mr-1" />
            Copiar Semana Anterior
          </Button>
          <Button variant="outline" onClick={exportScheduleExcel} data-testid="export-schedule-btn" className="text-xs">
            <Download className="w-4 h-4 mr-1" />
            Exportar Excel
          </Button>
        </div>
      </div>

      {/* Schedule Grids — uma escala separada por setor (SALA, BAR, COZINHA) */}
      {employees.length === 0 ? (
        <div className="border border-[#E5E7EB] p-8 text-center text-[#6B7280]">
          Nenhum funcionário ativo neste local
        </div>
      ) : (
        (() => {
          const departmentOrder = [
            { key: 'sala', label: 'SALA', color: 'bg-[#7C3AED]', border: 'border-[#7C3AED]' },
            { key: 'bar', label: 'BAR', color: 'bg-[#F59E0B]', border: 'border-[#F59E0B]' },
            { key: 'cozinha', label: 'COZINHA', color: 'bg-[#DC2626]', border: 'border-[#DC2626]' },
            { key: null, label: 'SEM SETOR', color: 'bg-[#9CA3AF]', border: 'border-[#9CA3AF]' },
          ];
          const positionOrder = {
            sala: ['Gerente', 'Chefe de Sala', 'Empregado de Mesa', 'Runner'],
            cozinha: ['Chef', 'Subchef', 'Cozinheiro', 'Ajudante de Cozinha', 'Copeiro'],
            bar: ['Chefe de Bar', 'Bartender'],
          };
          const groups = departmentOrder.map((dept) => {
            // Multi-setor: lista todos os funcionários cujo `departments` (lista) contenha o setor;
            // fallback para `department` (legado) ou null para "SEM SETOR".
            const list = employees.filter((e) => {
              const empDepts = (Array.isArray(e.departments) && e.departments.length > 0)
                ? e.departments
                : (e.department ? [e.department] : []);
              if (dept.key === null) return empDepts.length === 0;
              return empDepts.includes(dept.key);
            });
            if (dept.key && positionOrder[dept.key]) {
              const order = positionOrder[dept.key];
              list.sort((a, b) => {
                const ia = order.indexOf(a.position || '');
                const ib = order.indexOf(b.position || '');
                return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
              });
            }
            return { ...dept, list };
          });

          // Mostra sempre as 3 escalas oficiais; "SEM SETOR" só aparece se houver funcionários sem setor
          const visibleGroups = groups.filter((g) => g.key !== null || g.list.length > 0);

          return (
            <div className="space-y-12" data-testid="schedule-groups-container">
              {visibleGroups.map((group) => (
                <section
                  key={group.label}
                  className={`border-2 ${group.border} overflow-x-auto bg-white`}
                  data-testid={`schedule-section-${group.key || 'unassigned'}`}
                >
                  {/* Título da escala (setor) */}
                  <div
                    className={`${group.color} px-5 py-3 flex items-center justify-between`}
                    data-testid={`dept-header-${group.key || 'unassigned'}`}
                  >
                    <h2 className="text-white text-lg font-black uppercase tracking-widest" style={{ fontFamily: 'Satoshi, sans-serif' }}>
                      Escala {group.label}
                    </h2>
                    <span className="text-white/90 text-xs uppercase tracking-widest font-medium">
                      {group.list.length} {group.list.length === 1 ? 'funcionário' : 'funcionários'}
                    </span>
                  </div>

                  {/* Cabeçalho de dias dentro de cada escala */}
                  <div className="schedule-grid">
                    <div className="schedule-cell schedule-header">Funcionário</div>
                    {DAYS.map((day, idx) => (
                      <div key={day} className="schedule-cell schedule-header text-center">
                        <div>{DAY_LABELS[day]}</div>
                        <div className="text-[10px] text-[#6B7280]">
                          {format(addDays(currentWeek, idx), 'dd/MM')}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Linhas de funcionários do setor */}
                  {group.list.length === 0 ? (
                    <div className="p-6 text-center text-sm text-[#6B7280] italic">
                      Nenhum funcionário atribuído ao setor {group.label.toLowerCase()} neste local.
                    </div>
                  ) : (
                    group.list.map((emp) => (
                      <div key={emp.employee_id} className="schedule-grid">
                        <div className="schedule-cell flex flex-col gap-0.5">
                          <span className="font-medium text-sm">{emp.name}</span>
                          {emp.position && (
                            <span className="text-[10px] text-[#6B7280] uppercase tracking-wider">
                              {emp.position}
                            </span>
                          )}
                        </div>
                        {DAYS.map((day) => {
                          const shift = getShiftForDay(emp.employee_id, day);
                          const display = formatShiftDisplay(shift, group.key);
                          const isVacation = shift?.type === 'vacation';
                          const isAbsence = shift?.type === 'absence';
                          const isFolgaCell = !!(shift?.is_folga || shift?.type === 'folga');
                          const isOff = isVacation || isAbsence;
                          const lines = Array.isArray(display) ? display : (display ? [display] : []);
                          return (
                            <div
                              key={`${emp.employee_id}-${day}`}
                              className="schedule-cell cursor-pointer hover:bg-[#F3F4F6] transition-colors"
                              onClick={() => openEditDialog(emp.employee_id, day, group.key)}
                              data-testid={`schedule-cell-${emp.employee_id}-${day}`}
                            >
                              {lines.length > 0 ? (
                                <div
                                  className={`px-2 py-1 text-xs text-center font-medium flex flex-col gap-0.5 ${
                                    isVacation
                                      ? 'bg-[#F59E0B] text-white'
                                      : isAbsence
                                      ? 'bg-[#DC2626] text-white'
                                      : isFolgaCell
                                      ? 'bg-[#6B7280] text-white'
                                      : 'bg-[#0029FF] text-white'
                                  }`}
                                  data-testid={
                                    isOff
                                      ? `schedule-off-${emp.employee_id}-${day}-${shift.type}`
                                      : isFolgaCell
                                      ? `schedule-folga-${emp.employee_id}-${day}`
                                      : undefined
                                  }
                                >
                                  {lines.map((ln, i) => (
                                    <span key={`${emp.employee_id}-${day}-seg-${ln}-${i}`} className={lines.length > 1 ? 'leading-tight' : ''}>
                                      {ln}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <div className="flex items-center justify-center h-full text-[#9CA3AF] hover:text-[#0029FF]">
                                  <Plus className="w-4 h-4" />
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ))
                  )}
                </section>
              ))}
            </div>
          );
        })()
      )}

      {/* Edit Shift Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-black" style={{ fontFamily: 'Satoshi, sans-serif' }}>
              {editingEmployee && editingDay ? (
                <>{employees.find(e => e.employee_id === editingEmployee)?.name} — {DAY_LABELS[editingDay]}</>
              ) : 'Editar Turno'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-5 py-2">
            {/* Tipo de dia */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-widest text-[#111111]">Tipo de Dia</Label>
              <div className="grid grid-cols-4 gap-2">
                {[
                  { key: 'working', label: 'Turno', color: 'bg-[#0029FF]' },
                  { key: 'folga', label: 'Folga', color: 'bg-[#6B7280]' },
                  { key: 'vacation', label: 'Férias', color: 'bg-[#F59E0B]' },
                  { key: 'absence', label: 'Falta', color: 'bg-[#DC2626]' },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setDayType(opt.key)}
                    className={`py-2.5 text-xs font-bold uppercase tracking-widest border-2 transition-all ${
                      dayType === opt.key
                        ? `${opt.color} text-white border-transparent`
                        : 'bg-white text-[#111111] border-[#E5E7EB] hover:border-[#111111]'
                    }`}
                    data-testid={`day-type-${opt.key}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {dayType !== 'working' && (
                <p className="text-[11px] text-[#6B7280] italic">
                  {dayType === 'folga' && 'Dia de descanso semanal. Não conta horas.'}
                  {dayType === 'vacation' && 'Período de férias aprovado. Não conta horas.'}
                  {dayType === 'absence' && 'Falta registrada. Não conta horas.'}
                </p>
              )}
            </div>

            {/* Setor(es) disponíveis do funcionário */}
            {dayType === 'working' && (() => {
              const emp = employees.find((e) => e.employee_id === editingEmployee);
              const empDepts = (Array.isArray(emp?.departments) && emp.departments.length > 0)
                ? emp.departments
                : (emp?.department ? [emp.department] : []);
              if (empDepts.length < 2) return null;
              return (
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-widest text-[#111111]">Setor deste turno</Label>
                  <div className="flex gap-2 flex-wrap">
                    {empDepts.map((dk) => (
                      <button
                        key={dk}
                        type="button"
                        onClick={() => setSeg1Dept(dk)}
                        className={`px-3 py-1.5 text-xs font-bold uppercase tracking-widest border-2 transition-all ${
                          seg1Dept === dk
                            ? 'bg-[#0029FF] text-white border-[#0029FF]'
                            : 'bg-white text-[#111111] border-[#E5E7EB] hover:border-[#0029FF]'
                        }`}
                        data-testid={`seg1-dept-${dk}`}
                      >
                        {DEPT_LABELS[dk] || dk.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Presets */}
            {dayType === 'working' && (
              <div className="space-y-3">
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#111111]">Turnos Rápidos (aplicam ao 1º turno)</Label>
                <div className="grid grid-cols-2 gap-2">
                  {SHIFT_PRESETS.map((preset) => (
                    <Button
                      key={preset.label}
                      variant="outline"
                      className="h-10 text-sm justify-start font-medium hover:bg-[#0029FF] hover:text-white hover:border-[#0029FF]"
                      onClick={() => applyPreset(preset)}
                      data-testid={`preset-${preset.label}`}
                    >
                      {preset.label}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Segmento 1 */}
            {dayType === 'working' && (
              <div className="space-y-3 pt-2 border-t border-[#E5E7EB]">
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#111111]">1º Turno</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <span className="text-[10px] text-[#6B7280] uppercase tracking-wider">Entrada</span>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="abertura"
                        checked={isAbertura}
                        onCheckedChange={(v) => setIsAbertura(!!v)}
                        data-testid="abertura-checkbox"
                        className="h-5 w-5"
                      />
                      <label htmlFor="abertura" className="text-sm font-medium cursor-pointer">Abertura</label>
                    </div>
                    {!isAbertura && (
                      <Input
                        type="time"
                        value={seg1Start}
                        onChange={(e) => setSeg1Start(e.target.value)}
                        className="h-11 text-base font-semibold"
                        data-testid="seg1-start-input"
                      />
                    )}
                  </div>
                  <div className="space-y-2">
                    <span className="text-[10px] text-[#6B7280] uppercase tracking-wider">Saída</span>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="fecho"
                        checked={isFecho}
                        onCheckedChange={(v) => setIsFecho(!!v)}
                        data-testid="fecho-checkbox"
                        className="h-5 w-5"
                      />
                      <label htmlFor="fecho" className="text-sm font-medium cursor-pointer">Fecho</label>
                    </div>
                    {!isFecho && (
                      <Input
                        type="time"
                        value={seg1End}
                        onChange={(e) => setSeg1End(e.target.value)}
                        className="h-11 text-base font-semibold"
                        data-testid="seg1-end-input"
                      />
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Segmento 2 (horário repartido) */}
            {dayType === 'working' && !hasSeg2 && (
              <Button
                variant="outline"
                onClick={() => setHasSeg2(true)}
                className="w-full text-sm border-dashed"
                data-testid="add-second-shift-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Adicionar 2º turno (horário repartido)
              </Button>
            )}
            {dayType === 'working' && hasSeg2 && (() => {
              const emp = employees.find((e) => e.employee_id === editingEmployee);
              const empDepts = (Array.isArray(emp?.departments) && emp.departments.length > 0)
                ? emp.departments
                : (emp?.department ? [emp.department] : []);
              const showDept = empDepts.length >= 2;
              return (
                <div className="space-y-3 pt-2 border-t border-[#E5E7EB] bg-[#FFF7ED] p-3 -mx-1">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-semibold uppercase tracking-widest text-[#111111]">2º Turno</Label>
                    <button
                      type="button"
                      className="text-[10px] text-[#DC2626] uppercase font-bold tracking-wider hover:underline"
                      onClick={() => setHasSeg2(false)}
                      data-testid="remove-second-shift-btn"
                    >
                      Remover
                    </button>
                  </div>
                  {showDept && (
                    <div className="flex gap-2 flex-wrap">
                      {empDepts.map((dk) => (
                        <button
                          key={dk}
                          type="button"
                          onClick={() => setSeg2Dept(dk)}
                          className={`px-3 py-1 text-[10px] font-bold uppercase tracking-widest border-2 transition-all ${
                            seg2Dept === dk
                              ? 'bg-[#0029FF] text-white border-[#0029FF]'
                              : 'bg-white text-[#111111] border-[#E5E7EB] hover:border-[#0029FF]'
                          }`}
                          data-testid={`seg2-dept-${dk}`}
                        >
                          {DEPT_LABELS[dk] || dk.toUpperCase()}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <span className="text-[10px] text-[#6B7280] uppercase tracking-wider">Entrada</span>
                      <Input
                        type="time"
                        value={seg2Start}
                        onChange={(e) => setSeg2Start(e.target.value)}
                        className="h-11 text-base font-semibold"
                        data-testid="seg2-start-input"
                      />
                    </div>
                    <div className="space-y-1">
                      <span className="text-[10px] text-[#6B7280] uppercase tracking-wider">Saída</span>
                      <Input
                        type="time"
                        value={seg2End}
                        onChange={(e) => setSeg2End(e.target.value)}
                        className="h-11 text-base font-semibold"
                        data-testid="seg2-end-input"
                      />
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Preview */}
            <div className="p-4 bg-[#F3F4F6] border-2 border-[#E5E7EB]">
              <Label className="text-xs font-semibold uppercase tracking-widest text-[#6B7280] mb-2 block">Pré-visualização</Label>
              {dayType === 'folga' ? (
                <span className="text-2xl font-black" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>F · FOLGA</span>
              ) : dayType === 'vacation' ? (
                <span className="text-2xl font-black text-[#F59E0B]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>FÉRIAS</span>
              ) : dayType === 'absence' ? (
                <span className="text-2xl font-black text-[#DC2626]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>FALTA</span>
              ) : (
                <div className="flex flex-col gap-1" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                  <span className="text-2xl font-black">
                    {isAbertura ? 'Abertura' : seg1Start} — {isFecho ? 'Fecho' : seg1End}
                  </span>
                  {hasSeg2 && (
                    <span className="text-2xl font-black text-[#F59E0B]">
                      {seg2Start} — {seg2End}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="gap-2 pt-4 border-t border-[#E5E7EB]">
            {getShiftForDay(editingEmployee, editingDay) && (
              <Button variant="destructive" onClick={removeShift} data-testid="remove-shift-btn" className="mr-auto px-5">
                <Trash2 className="w-4 h-4 mr-2" />
                Remover
              </Button>
            )}
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="px-6">Cancelar</Button>
            <Button onClick={saveShift} data-testid="save-shift-btn" className="px-6 bg-[#0029FF] hover:bg-[#0022CC]">
              Salvar Turno
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
